import React from 'react';
import {
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Tooltip,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallSplitIcon from '@mui/icons-material/CallSplit';

interface CsvColumnSelectorProps {
  csvHeaders: string[];
  csvSampleData?: Record<string, string>[];
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiSelect?: boolean;
  matchConfidence?: number;
  isCoordinateSplit?: boolean;
  label?: string;
  disabled?: boolean;
}

function getSampleValue(header: string, sampleData?: Record<string, string>[]): string {
  if (!sampleData || sampleData.length === 0) return '';
  for (const row of sampleData) {
    const val = row[header];
    if (val !== undefined && val !== null && val.trim() !== '') {
      return val.length > 40 ? val.substring(0, 37) + '...' : val;
    }
  }
  return '';
}

const CsvColumnSelector: React.FC<CsvColumnSelectorProps> = ({
  csvHeaders,
  csvSampleData,
  value,
  onChange,
  multiSelect = false,
  matchConfidence,
  isCoordinateSplit = false,
  label,
  disabled = false,
}) => {
  const hasMatch = matchConfidence !== undefined && matchConfidence >= 0.7;

  const renderMatchIndicator = () => {
    if (isCoordinateSplit) {
      return (
        <Tooltip title="Divisor: los valores de esta columna se dividiran en latitud y longitud">
          <CallSplitIcon fontSize="small" color="info" sx={{ ml: 0.5 }} />
        </Tooltip>
      );
    }
    if (hasMatch) {
      return (
        <Tooltip title={`Coincidencia inteligente (${Math.round((matchConfidence ?? 0) * 100)}%)`}>
          <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 0.5 }} />
        </Tooltip>
      );
    }
    return null;
  };

  if (multiSelect) {
    const selectedValues = Array.isArray(value) ? value : [];
    return (
      <Autocomplete
        multiple
        size="small"
        options={csvHeaders}
        value={selectedValues}
        disabled={disabled}
        onChange={(_, newValue) => onChange(newValue.length > 0 ? newValue : null)}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            const { key, ...rest } = getTagProps({ index });
            return <Chip key={key} label={tag} size="small" {...rest} />;
          })
        }
        renderOption={(props, option) => {
          const { key, ...rest } = props as any;
          const sample = getSampleValue(option, csvSampleData);
          return (
            <Box component="li" key={key} {...rest} sx={{ minHeight: 'auto !important' }}>
              <ListItemText
                primary={option}
                secondary={sample ? `ej. ${sample}` : null}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption', color: 'textSecondary', fontStyle: 'italic' }}
              />
            </Box>
          );
        }}
        renderInput={(params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              {...params}
              label={label}
              placeholder={csvHeaders.length > 0 ? 'Seleccionar columnas...' : 'Sin columnas CSV'}
              variant="outlined"
            />
            {renderMatchIndicator()}
          </Box>
        )}
      />
    );
  }

  const singleValue = typeof value === 'string' ? value : '';
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InputLabel>{label}</InputLabel>
          {renderMatchIndicator()}
        </Box>
      )}
      <Select
        value={singleValue}
        label={label}
        onChange={(e) => onChange(e.target.value || null)}
        startAdornment={hasMatch ? <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 0.5 }} /> : null}
        renderValue={(selected) => {
          if (!selected) return <em>--- Ninguno ---</em>;
          return selected as string;
        }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 400 },
          },
        }}
      >
        <MenuItem value="">
          <ListItemText primary={<em>--- Ninguno ---</em>} />
        </MenuItem>
        {csvHeaders.map((header) => {
          const sample = getSampleValue(header, csvSampleData);
          return (
            <MenuItem key={header} value={header}>
              <ListItemText
                primary={header}
                secondary={sample ? `ej. ${sample}` : null}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption', color: 'textSecondary', fontStyle: 'italic' }}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default CsvColumnSelector;
